/**
 * انتخابِ چندتایی از یه فهرست، با جستجو — قرینه‌ی
 * apps/mobile/src/ui/MultiSelect.tsx.
 *
 * از دلِ `TagPicker` در اومد بیرون وقتی بخشِ دعوت هم همین رفتار رو خواست:
 * یه سطرِ جمع‌شده که انتخاب‌شده‌ها رو چیپ‌چیپ نشون می‌ده (هر کدوم با ضربدرِ
 * خودش)، و بازشو با جستجو. تگ و دوست دو تا فهرستِ متفاوتن ولی رفتارِ
 * انتخابشون یکیه، و دو کپیِ همین صد خط یعنی دو تا رفتارِ کمی متفاوت.
 *
 * `onToggle` و نه `onChange(list)`: صداکننده گاهی خودش می‌دونه با هر انتخاب
 * چه کار کنه (مثلاً دوستِ بی‌حساب می‌ره توی فهرستِ پیامک، نه فهرستِ دعوت).
 */
import React from 'react';
import { tapped } from '@/core/lib/devlog';
import { t } from '@/core/lib/i18n';
import { useOutsideClose } from './useOutsideClose';

export interface PickItem {
  key: string;
  label: string;
  /** متنی که جستجو روش می‌گرده. نیامده یعنی خودِ `label`. */
  searchText?: string;
  /** خطِ دومِ کم‌رنگِ داخلِ فهرستِ باز — مثلاً «۵ نفر» برای یه گروه. */
  hint?: string;
  /**
   * دیده می‌شه ولی انتخاب‌شدنی نیست — خاکستری، بی‌کلیک. برای ردیفی که
   * وجودش خبره ولی از این‌جا کاری باهاش نمی‌شه کرد (دوستِ فقط-شماره‌تلفن
   * روی وب، WHOSUP-143). `hint` جای توضیحِ «چرا»ست.
   */
  disabled?: boolean;
}

export function MultiSelect({
  value, items, onToggle, placeholder, logKey = 'pick', filter, chipKeys,
}: {
  value: string[];
  items: PickItem[];
  onToggle: (key: string) => void;
  /** متنِ سطر وقتی هیچی انتخاب نشده. */
  placeholder: string;
  /** پیشوندِ لاگِ فعالیت، تا دو تا انتخابگر توی یه صفحه از هم جدا باشن. */
  logKey?: string;
  /** جستجوی سفارشی؛ نیامده یعنی همون `searchText ?? label`. */
  filter?: (items: PickItem[], q: string) => PickItem[];
  /**
   * کدوم انتخاب‌ها چیپ بگیرن. نیامده یعنی همه‌ی `value`.
   *
   * برای وقتی یه انتخاب، انتخاب‌های دیگه‌ای رو **در بر می‌گیره**: گروهی که
   * انتخاب شده یعنی هر پنج عضوش دعوتن (و توی فهرست هم تیک دارن)، ولی پنج
   * چیپِ اضافه کنارِ چیپِ گروه فقط سطر رو شلوغ می‌کنه.
   */
  chipKeys?: string[];
}) {
  const [open, setOpen] = React.useState(false);
  const [q, setQ] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  useOutsideClose(ref, open, () => setOpen(false));

  const chipsOf = chipKeys ?? value;
  const selected = items.filter((it) => chipsOf.includes(it.key));
  const needle = q.trim().toLowerCase();
  const shown = filter
    ? filter(items, q)
    : (needle
      ? items.filter((it) => (it.searchText ?? it.label).toLowerCase().includes(needle))
      : items);

  const openToggle = tapped(`${logKey}:open`, () => { setQ(''); setOpen((x) => !x); });

  return (
    <div className="select" ref={ref}>
      {/* دکمه نیست، div است: هر انتخاب‌شده ضربدرِ خودش رو داره (بازخورد
          ۲۰۲۶-۰۸-۲۱) و دکمه‌ی تو در تو HTML معتبری نیست. نقش و کیبورد
          دستی داده شده تا رفتارش همون دکمه بمونه. */}
      <div
        className={`select-trigger tagpick${open ? ' open' : ''}${selected.length ? '' : ' empty'}`}
        role="button" tabIndex={0} aria-haspopup="listbox" aria-expanded={open}
        onClick={openToggle}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openToggle(); }
        }}>
        {selected.length ? (
          <span className="tagpick-chips">
            {selected.map((it) => (
              <span key={it.key} className="tagpick-chip">
                {it.label}
                <button type="button" className="tagpick-x"
                  aria-label={`${t('remove')} — ${it.label}`}
                  onClick={tapped(`${logKey}:remove`, (e: React.MouseEvent) => {
                    // برداشتنِ چیپ نباید منو رو باز/بسته کنه.
                    e.stopPropagation();
                    onToggle(it.key);
                  })}>
                  ×
                </button>
              </span>
            ))}
          </span>
        ) : <span>{placeholder}</span>}
        <span className="select-chev" aria-hidden>▾</span>
      </div>

      {open ? (
        <div className="menu select-menu">
          <input className="input" placeholder={t('searchHint')} value={q} autoFocus
            onChange={(e) => setQ(e.target.value)} />
          <div className="select-options" role="listbox" aria-multiselectable>
            {shown.length === 0 ? <p className="dim">{t('searchNoMatch')}</p> : null}
            {shown.map((it) => {
              const on = value.includes(it.key);
              return (
                <button key={it.key} type="button" role="option" aria-selected={on}
                  disabled={it.disabled}
                  className={`menu-item${on ? ' active' : ''}`}
                  // برخلافِ Select عمداً باز می‌مونه: چندتایی یعنی چند کلیکِ پشتِ هم.
                  onClick={tapped(`${logKey}:${it.key}`, () => onToggle(it.key))}>
                  {it.label}{on ? ' ✓' : ''}
                  {it.hint ? <small>{it.hint}</small> : null}
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
